﻿import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { NO_SEARCH_RESULTS, TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import {
    DateFormatter,
    TimeFormatter,
    convertToSentenceCase,
} from "@/utils/functions/helperFunctions";
import { updateUserById } from "@/services/user.service";
import { fetchConfig } from "@/services/setting.service";
import { errorToasterMessage } from "@/utils/functions/toaster";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import NoFilterDataCard from "@/components/cards/NoFilterDataCard";
import { MobileDateField, MobileLabelValue } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    UsersTableSkeletonDesktop,
    UsersTableSkeletonMobile,
} from "@/components/skeletons";

interface UserTableProps {
    allFetched: boolean;
    users: any[];
    setUsers: (users: any) => void;
    search: (searchText: string) => void;
    reset: () => void;
    isLoading: boolean;
    dataLength: number;
    sorting: any;
    setSorting: (sorting: any) => void;
    searchItem: string | null;
    title: string;
    setPageNo: (pageNo: number) => void;
    dataload: boolean;
    resetDataQueries: () => void;
    id: string;
    is_allow_manage_shared_access: boolean;
}

const getUserOrg = (user: any) => user?.organizations?.[0];

const UsersTable: React.FC<UserTableProps> = (props) => {
    const [email_addressStatus, setemail_addressStatus] = React.useState(true);
    const [lastChangeStatus, setLastChangeStatus] = React.useState(true);
    const [createdAtStatus, setCreatedAtStatus] = React.useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");
    const [isEnabled, setIsEnabled] = React.useState(false);
    const isCompactLayout = useCompactListLayout();
    const navigate = useNavigate();

    const users = Array.isArray(props.users) ? props.users : [];
    const showActionColumn = props.is_allow_manage_shared_access && isEnabled;
    const columnCount = showActionColumn ? 7 : 6;
    const isPendingInitialLoad = props.dataload || (!users.length && props.isLoading);
    const isLoadingMore = props.isLoading && !props.dataload;

    const emailAddressSort = () => {
        setSortElement("email_address");
        props.setPageNo(1);
        props.setSorting({ email_address: !email_addressStatus ? 1 : -1 });
        setemail_addressStatus(!email_addressStatus);
    };

    const lastChangeSort = () => {
        setLastChangeStatus(!lastChangeStatus);
        setSortElement("lastlogin");
        props.setPageNo(1);
        props.setSorting({ "login.last_login_at": !lastChangeStatus ? 1 : -1 });
    };

    const createdAtSort = () => {
        setCreatedAtStatus((prevStatus) => !prevStatus);
        setSortElement("created_at");
        props.setPageNo(1);
        props.setSorting({ created_at: createdAtStatus ? 1 : -1 });
    };

    useEffect(() => {
        fetchConfig(
            null,
            (res: any) => setIsEnabled(Boolean(res?.is_shared_access_enabled)),
            (err: any) => errorToasterMessage(err?.response?.data?.message)
        );
    }, []);

    const statusChange = (userId: string, currentStatus: boolean) => {
        const nextStatus = !currentStatus;

        props.setUsers(
            users.map((user: any) => {
                if (user.id !== userId) return user;
                const org = getUserOrg(user);
                return {
                    ...user,
                    organizations: org
                        ? [{ ...org, is_active: nextStatus }]
                        : user.organizations,
                };
            })
        );

        updateUserById(
            userId,
            { is_active: nextStatus },
            () => {},
            (error: any) => {
                props.setUsers(
                    users.map((user: any) => {
                        if (user.id !== userId) return user;
                        const org = getUserOrg(user);
                        return {
                            ...user,
                            organizations: org
                                ? [{ ...org, is_active: currentStatus }]
                                : user.organizations,
                        };
                    })
                );
                errorToasterMessage(
                    error?.response?.data?.message ?? "Failed to update user status"
                );
            }
        );
    };

    const renderStatusSwitch = (user: any, index: number) => {
        const active = Boolean(getUserOrg(user)?.is_active);
        return (
            <span className="gs-prod-switch-wrap">
                <Switch
                    className="gs-prod-switch"
                    id={`${props.id}-status-toggle-${index}`}
                    checked={active}
                    onCheckedChange={() => statusChange(user.id, active)}
                    aria-label={`Toggle status for ${user.email_address}`}
                />
            </span>
        );
    };

    if (isCompactLayout) {
        if (isPendingInitialLoad) {
            return (
                <div className="gs-users-table-wrap">
                    <UsersTableSkeletonMobile />
                </div>
            );
        }

        if (!users.length) {
            return (
                <NoDataAvailable>
                    {props.searchItem ? (
                        <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                    ) : (
                        <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                    )}
                </NoDataAvailable>
            );
        }

        return (
            <div className="gs-users-table-wrap">
                {users.map((user: any, key: number) => (
                    <div className="gs-users-mobile-card gs-mobile-list-card" key={user.id ?? key}>
                        <div className="gs-mobile-card-icon">
                            <em
                                id={`${props.id}-icon-${key}-mb`}
                                className={`icon ni ${props.title === "Users" ? "ni-user" : "ni-account-setting"}`}
                            />
                        </div>
                        <div className="gs-users-mobile-meta">
                            <p className="gs-users-table-user-email m-0" id={`${props.id}-email-${key}-mb`}>
                                {user.email_address}
                            </p>
                            <p className="gs-users-table-user-name m-0" id={`${props.id}-user-name-${key}-mb`}>
                                {convertToSentenceCase(
                                    `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                                )}
                            </p>
                            <MobileLabelValue
                                label="Roles: "
                                value={getUserOrg(user)?.roles?.length ?? 0}
                                id={`${props.id}-role-count-${key}-mb`}
                            />
                            <MobileLabelValue
                                label="Groups: "
                                value={getUserOrg(user)?.groups?.length ?? 0}
                                id={`${props.id}-group-count-${key}-mb`}
                            />
                            <div className="text-dark gs-users-mobile-status">
                                <span className="fw-bold">{TABLE_HEADERS.STATUS}: </span>
                                {renderStatusSwitch(user, key)}
                            </div>
                            <MobileDateField
                                label="Created At: "
                                value={user.created_at}
                                dateId={`${props.id}-created-at-date-${key}-mb`}
                                timeId={`${props.id}-created-at-time-${key}-mb`}
                            />
                            <MobileDateField
                                label="Last Login At: "
                                value={user?.login?.last_login_at}
                                dateId={`${props.id}-last-login-date-${key}-mb`}
                                timeId={`${props.id}-last-login-time-${key}-mb`}
                            />
                        </div>
                        <MobileListChevron
                            id={`${props.id}-redirect-${key}-mb`}
                            onClick={() => navigate(`/user/${user.id}`)}
                        />
                    </div>
                ))}
                {isLoadingMore ? <UsersTableSkeletonMobile rowCount={2} /> : null}
            </div>
        );
    }

    return (
        <div className="gs-users-table-wrap">
        <table
            className={`gs-users-table gs-nk-ulist-table gs-users-list-table gs-users-list-table--cols-${columnCount}`}
            style={{ tableLayout: "fixed", width: "100%" }}
        >
            <colgroup>
                <col className="gs-users-list-col-user" />
                <col className="gs-users-list-col-role" />
                <col className="gs-users-list-col-group" />
                <col className="gs-users-list-col-status" />
                <col className="gs-users-list-col-created" />
                <col className="gs-users-list-col-login" />
                {showActionColumn ? <col className="gs-users-list-col-action" /> : null}
            </colgroup>
            <thead>
                <tr>
                    <th className="gs-users-th gs-ulist-col-user cursor-pointer" onClick={emailAddressSort}>
                        <span id={`${props.id}-user-email-th`}>{TABLE_HEADERS.USER}</span>
                        <em
                            id={`${props.id}-user-email-th-sort-icon`}
                            className={`ml-1 icon ni ${sortElement === "email_address" ? (email_addressStatus ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                        />
                    </th>
                    <th className="gs-users-th gs-ulist-col-role">
                        <span id={`${props.id}-role-th`}>{TABLE_HEADERS.ROLE}</span>
                    </th>
                    <th className="gs-users-th gs-ulist-col-group">
                        <span id={`${props.id}-group-th`}>{TABLE_HEADERS.GROUP}</span>
                    </th>
                    <th className="gs-users-th gs-ulist-col-status">
                        <span id={`${props.id}-status-th`}>{TABLE_HEADERS.STATUS}</span>
                    </th>
                    <th className="gs-users-th gs-ulist-col-created cursor-pointer" onClick={createdAtSort}>
                        <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                        <em
                            id={`${props.id}-created-at-th-sort-icon`}
                            className={`ml-1 icon ni ${sortElement === "created_at" ? (createdAtStatus ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                        />
                    </th>
                    <th className="gs-users-th gs-ulist-col-login cursor-pointer" onClick={lastChangeSort}>
                        <span id={`${props.id}-last-login-th`}>{TABLE_HEADERS.LAST_LOGIN_AT}</span>
                        <em
                            id={`${props.id}-last-login-th-sort-icon`}
                            className={`ml-1 icon ni ${sortElement === "lastlogin" ? (lastChangeStatus ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                        />
                    </th>
                    {showActionColumn ? (
                        <th className="gs-users-th gs-ulist-col-action" />
                    ) : null}
                </tr>
            </thead>
            <tbody>
                {!isPendingInitialLoad ? (
                    users.length > 0 ? (
                        <>
                            {users.map((user: any, key: number) => (
                                <tr key={user.id ?? key}>
                                    <td className="gs-users-td gs-ulist-col-user">
                                        <Link to={`/user/${user.id}`} className="gs-users-table-user-cell">
                                            <div className="gs-users-table-user-icon">
                                                <em
                                                    id={`${props.id}-icon-${key}`}
                                                    className={`icon ni ${props.title === "Users" ? "ni-user" : "ni-account-setting"}`}
                                                />
                                            </div>
                                            <div className="gs-users-table-user-info">
                                                <span className="gs-users-table-user-email" id={`${props.id}-email-${key}`}>
                                                    {user.email_address}
                                                </span>
                                                <span className="gs-users-table-user-name" id={`${props.id}-user-name-${key}`}>
                                                    {convertToSentenceCase(
                                                        `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                                                    )}
                                                </span>
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="gs-users-td gs-ulist-col-role">
                                        <span id={`${props.id}-role-count-${key}`}>
                                            {getUserOrg(user)?.roles?.length ?? 0}
                                        </span>
                                    </td>
                                    <td className="gs-users-td gs-ulist-col-group">
                                        <span id={`${props.id}-group-count-${key}`}>
                                            {getUserOrg(user)?.groups?.length ?? 0}
                                        </span>
                                    </td>
                                    <td className="gs-users-td gs-ulist-col-status">
                                        {renderStatusSwitch(user, key)}
                                    </td>
                                    <td className="gs-users-td gs-ulist-col-created">
                                        <span className="gs-users-table-date" id={`${props.id}-created-at-date-${key}`}>
                                            {DateFormatter(user?.created_at)}
                                        </span>
                                        <span className="gs-users-table-date-sub" id={`${props.id}-created-at-time-${key}`}>
                                            {TimeFormatter(user?.created_at)}
                                        </span>
                                    </td>
                                    <td className="gs-users-td gs-ulist-col-login">
                                        <span className="gs-users-table-date" id={`${props.id}-last-login-date-${key}`}>
                                            {DateFormatter(user?.login?.last_login_at)}
                                        </span>
                                        <span className="gs-users-table-date-sub" id={`${props.id}-last-login-time-${key}`}>
                                            {TimeFormatter(user?.login?.last_login_at)}
                                        </span>
                                    </td>
                                    {showActionColumn ? (
                                        <td className="gs-users-td gs-ulist-col-action">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="default"
                                                        disabled={!user.is_verified}
                                                        id={`action-dropdown-btn-${key}`}
                                                    >
                                                        Action
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem
                                                        id={`action-dropdown-manage-shared-access-${key}`}
                                                        onSelect={() =>
                                                            navigate(`/shared-access/${user.id}`, {
                                                                replace: true,
                                                                state: { user },
                                                            })
                                                        }
                                                    >
                                                        Manage Shared Access
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </td>
                                    ) : null}
                                </tr>
                            ))}
                            {isLoadingMore ? (
                                <UsersTableSkeletonDesktop
                                    rowCount={2}
                                    showActionColumn={showActionColumn}
                                />
                            ) : null}
                        </>
                    ) : (
                        <tr>
                            <td colSpan={columnCount}>
                                <NoDataAvailable>
                                    {props.searchItem ? (
                                        <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                                    ) : (
                                        <NoFilterDataCard
                                            id={props.id}
                                            loadAll={props.resetDataQueries}
                                        />
                                    )}
                                </NoDataAvailable>
                            </td>
                        </tr>
                    )
                ) : (
                    <UsersTableSkeletonDesktop showActionColumn={showActionColumn} />
                )}
            </tbody>
        </table>
        </div>
    );
};

export default UsersTable;
